import { useState } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next';

interface PhotoGalleryProps {
  photos: string[];
  canEdit?: boolean;
  onDeletePhoto?: (photoUrl: string) => void;
}

export default function PhotoGallery({ photos, canEdit = false, onDeletePhoto }: PhotoGalleryProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  if (!photos.length) return null;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((photo, index) => (
          <div
            key={photo}
            className="relative aspect-square group"
          >
            <img
              src={photo}
              alt={t('photos.photo_number', { number: index + 1 })}
              className="w-full h-full object-cover rounded-lg cursor-pointer"
              onClick={() => {
                setSelectedPhotoIndex(index);
                setIsOpen(true);
              }}
            />
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg" />
            {canEdit && onDeletePhoto && (
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeletePhoto(photo);
                }}
                aria-label={t('photos.delete_photo')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl w-[95vw] p-2 bg-black/95">
          <Carousel className="w-full" opts={{ startIndex: selectedPhotoIndex }}>
            <CarouselContent>
              {photos.map((photo, index) => (
                <CarouselItem key={photo}>
                  <div className="flex items-center justify-center p-1">
                    <img
                      src={photo}
                      alt={t('photos.photo_number', { number: index + 1 })}
                      className="max-h-[80vh] object-contain rounded-lg"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
        </DialogContent>
      </Dialog>
    </>
  );
}